/**
 * Dates du module Congés — la SEULE arithmétique de dates du module (R-FER-03).
 *
 * FONCTIONS PURES, aucune dépendance. Le dépôt n'a pas de bibliothèque de dates
 * et n'en ajoute pas : aucune ne fournit les jours fériés français, l'algorithme
 * de Pâques serait à écrire de toute façon.
 *
 * DEUX PIÈGES que ce fichier existe pour neutraliser :
 *
 *  1. LE FUSEAU. Scalingo tourne en UTC. `new Date().toLocaleDateString()` y
 *     donne la date UTC : un congé posé le 1er à 01 h 00 heure de Paris serait
 *     compté au 31 du mois précédent. Toute date « du jour » passe donc par
 *     `aujourdhui()`, et tout jour tiré d'un horodatage par `jourAParis()`, qui
 *     demandent explicitement Europe/Paris.
 *
 *  2. L'HEURE D'ÉTÉ. Ajouter 24 h en millisecondes à une date locale saute ou
 *     répète un jour aux changements d'heure (mars et octobre). Toute
 *     l'arithmétique se fait donc sur des dates UTC à midi, où un décalage
 *     d'une heure ne peut jamais faire basculer le quantième.
 *
 * Les dates circulent en chaînes `AAAA-MM-JJ` : c'est déjà la convention du
 * dépôt (`ndf_delegations.debut`, `ndf_depenses.date_depense`), elles se
 * comparent alphabétiquement, et elles ne portent ni heure ni fuseau à perdre.
 */

/** Une date au format `AAAA-MM-JJ`. */
export type DateIso = string;

const JOUR_MS = 86_400_000;

/** Fabrique une date UTC à midi — voir le piège n°2 de l'en-tête. */
function utc(annee: number, mois: number, jour: number): Date {
  return new Date(Date.UTC(annee, mois - 1, jour, 12));
}

function versIso(d: Date): DateIso {
  return d.toISOString().slice(0, 10);
}

/** `AAAA-MM-JJ` → Date UTC à midi. Lance si la chaîne n'est pas une date. */
export function depuisIso(date: DateIso): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`Date attendue au format AAAA-MM-JJ, reçu : ${date}`);
  const [, a, mo, j] = m;
  const d = utc(Number(a), Number(mo), Number(j));
  // Garde-fou : le 31 février deviendrait le 3 mars sans lever d'erreur, et la
  // faute se verrait des mois plus tard sur un décompte.
  if (versIso(d) !== date) throw new Error(`Date inexistante : ${date}`);
  return d;
}

/**
 * Le jour, à Paris, où tombe un INSTANT.
 *
 * C'est la seule façon correcte de tirer une date d'un horodatage. Découper la
 * chaîne ISO (`slice(0, 10)`) lit le jour en temps UNIVERSEL : Paris étant à
 * UTC+1 ou +2, tout ce qui se passe entre minuit et deux heures du matin ressort
 * à la VEILLE. Ce n'est pas un cas d'école — le planificateur passe tôt le
 * matin, et la RH lisait « dernière relance le 19 » pour une relance partie
 * le 20.
 *
 * `sv-SE` est la locale suédoise : c'est la seule courante dont le format natif
 * est déjà `AAAA-MM-JJ`. L'astuce est employée ailleurs dans le dépôt ; ce qui
 * change ici, c'est le fuseau, explicite.
 */
export function jourAParis(instant: Date): DateIso {
  return instant.toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
}

/** La date du jour à Paris — le piège n°1 de l'en-tête, pour « maintenant ». */
export function aujourdhui(maintenant: Date = new Date()): DateIso {
  return jourAParis(maintenant);
}

export function ajouterJours(date: DateIso, nombre: number): DateIso {
  return versIso(new Date(depuisIso(date).getTime() + nombre * JOUR_MS));
}

/**
 * Combien de jours séparent deux dates : `jusqua − depuis`, signé.
 *
 * Des JOURS DE CALENDRIER, pas une durée — ce que compterait un humain en
 * tournant les pages d'un agenda. Les deux bornes passent par `depuisIso`, donc
 * par le même midi UTC (piège n°2), et aucun changement d'heure ne peut décaler
 * le résultat. Mesurer l'écart entre deux INSTANTS réels donnerait 6,9 jours là
 * où il y en a 7, et un arrondi vers le bas ferait d'une cadence hebdomadaire
 * une cadence de huit jours : c'est ce qui est arrivé aux relances de
 * justificatif.
 */
export function ecartEnJours(depuis: DateIso, jusqua: DateIso): number {
  return Math.round((depuisIso(jusqua).getTime() - depuisIso(depuis).getTime()) / JOUR_MS);
}

/** Dernier jour du mois, utile aux acquisitions mensuelles (R-CPT-02). */
export function finDeMois(annee: number, mois: number): DateIso {
  // Le jour 0 du mois suivant EST le dernier jour du mois demandé : pas de
  // table de longueurs de mois à maintenir, et les années bissextiles sont
  // traitées par le calendrier lui-même.
  return versIso(new Date(Date.UTC(annee, mois, 0, 12)));
}

/**
 * Dimanche de Pâques (algorithme de Meeus/Jones/Butcher, calendrier grégorien).
 *
 * Trois des onze fériés français en dépendent. L'algorithme est recopié tel
 * quel et vérifié par les tests sur plusieurs années : le modifier « pour le
 * simplifier » est le meilleur moyen de décaler un jour férié.
 */
export function paques(annee: number): DateIso {
  const a = annee % 19;
  const b = Math.floor(annee / 100);
  const c = annee % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mois = Math.floor((h + l - 7 * m + 114) / 31);
  const jour = ((h + l - 7 * m + 114) % 31) + 1;
  return versIso(utc(annee, mois, jour));
}

export interface OptionsFeries {
  /**
   * Le Lundi de Pentecôte est-il chômé ? (R-FER-02)
   *
   * Chez Kestio OUI — jour offert, la journée de solidarité étant portée par le
   * +1 du forfait jour. Le paramètre existe parce que c'est un choix
   * d'entreprise, pas une règle légale : une autre année pourrait le travailler.
   */
  lundiPentecoteChome?: boolean;
}

/**
 * Les jours fériés français d'une année (R-FER-01), calculés à la volée.
 *
 * Jamais mis en cache : le calcul est déterministe et instantané, alors qu'une
 * table à resynchroniser diverge en silence. Arbitrage tracé dans les specs
 * v1.3.
 *
 * Pas de spécificité Alsace-Moselle : aucun salarié Kestio n'y est rattaché.
 */
export function joursFeries(annee: number, options: OptionsFeries = {}): Map<DateIso, string> {
  const { lundiPentecoteChome = true } = options;
  const feries = new Map<DateIso, string>();

  feries.set(versIso(utc(annee, 1, 1)), 'Jour de l’an');
  feries.set(versIso(utc(annee, 5, 1)), 'Fête du Travail');
  feries.set(versIso(utc(annee, 5, 8)), 'Victoire 1945');
  feries.set(versIso(utc(annee, 7, 14)), 'Fête nationale');
  feries.set(versIso(utc(annee, 8, 15)), 'Assomption');
  feries.set(versIso(utc(annee, 11, 1)), 'Toussaint');
  feries.set(versIso(utc(annee, 11, 11)), 'Armistice 1918');
  feries.set(versIso(utc(annee, 12, 25)), 'Noël');

  const dimanchePaques = paques(annee);
  feries.set(ajouterJours(dimanchePaques, 1), 'Lundi de Pâques');
  feries.set(ajouterJours(dimanchePaques, 39), 'Ascension');
  if (lundiPentecoteChome) {
    feries.set(ajouterJours(dimanchePaques, 50), 'Lundi de Pentecôte');
  }

  return feries;
}

export function estFerie(date: DateIso, options?: OptionsFeries): boolean {
  return joursFeries(Number(date.slice(0, 4)), options).has(date);
}

/** Samedi ou dimanche. */
export function estWeekend(date: DateIso): boolean {
  const jour = depuisIso(date).getUTCDay();
  return jour === 0 || jour === 6;
}

/** Jour travaillable : du lundi au vendredi, hors férié (D2.4). */
export function estOuvre(date: DateIso, options?: OptionsFeries): boolean {
  return !estWeekend(date) && !estFerie(date, options);
}

/**
 * Les jours ouvrés d'une période, bornes INCLUSES.
 *
 * R-FER-03 : tout décompte de congé, tout calcul de tickets restaurant et tout
 * calcul de RTT passe par cette fonction. Aucune ré-implémentation locale —
 * c'est ce qui garantit qu'un jour férié ne sera jamais compté deux fois
 * différemment selon l'écran.
 */
export function listerJoursOuvres(
  debut: DateIso,
  fin: DateIso,
  options?: OptionsFeries,
): DateIso[] {
  if (fin < debut) return [];
  // Les fériés sont calculés une fois par année traversée, pas une fois par
  // jour : sur une période d'un an, cela évite 365 recalculs identiques.
  const cache = new Map<number, Map<DateIso, string>>();
  const ferieUnJour = (d: DateIso) => {
    const annee = Number(d.slice(0, 4));
    let f = cache.get(annee);
    if (!f) {
      f = joursFeries(annee, options);
      cache.set(annee, f);
    }
    return f.has(d);
  };

  const jours: DateIso[] = [];
  for (let d = debut; d <= fin; d = ajouterJours(d, 1)) {
    if (!estWeekend(d) && !ferieUnJour(d)) jours.push(d);
  }
  return jours;
}

/** Nombre de jours ouvrés d'une période, bornes incluses (R-FER-03). */
export function joursOuvres(debut: DateIso, fin: DateIso, options?: OptionsFeries): number {
  return listerJoursOuvres(debut, fin, options).length;
}
